import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import {
  StreamI18nProvider,
  getDirection,
  persistPaymentLinkLang,
  setLanguage,
} from "./_stream/i18n/Streami18n";
import { RootContext, RootStore } from "./store";
import { initAnalytics } from "./_stream/helpers/analytics";
import { QueryClient, QueryClientProvider } from "react-query";
import { initEmbedShell } from "./utils/embedMode";

initEmbedShell();

initAnalytics({
  fullstoryOrgId: import.meta.env.VITE_FULLSTORY_ORG_ID,
  posthogKey: import.meta.env.VITE_POSTHOG_KEY,
  posthogHost: import.meta.env.VITE_POSTHOG_HOST,
  mixpanelToken: import.meta.env.VITE_MIXPANEL_TOKEN,
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
});

window.addEventListener("vite:preloadError", (event) => {
  if (!sessionStorage.getItem("chunk_reloaded")) {
    event.preventDefault(); // suppress error on first attempt — clean reload, no error boundary flash
    sessionStorage.setItem("chunk_reloaded", "1");
    window.location.reload();
  }
  // second attempt: no preventDefault → error propagates to error boundary
});

window.addEventListener("unhandledrejection", (event) => {
  const msg = event.reason?.message ?? "";
  if (
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed")
  ) {
    if (!sessionStorage.getItem("chunk_reloaded")) {
      event.preventDefault();
      sessionStorage.setItem("chunk_reloaded", "1");
      window.location.reload();
    }
  }
});

const urlLang = new URLSearchParams(window.location.search)
  .get("language")
  ?.toLowerCase();
if (urlLang === "ar" || urlLang === "en") {
  setLanguage(urlLang);
  if (window.location.pathname.includes("/payment-link")) {
    persistPaymentLinkLang(urlLang);
  }
}

const bootDirection = getDirection();
document.body.setAttribute("dir", bootDirection);
document.body.style.direction = bootDirection;

const queryClient = new QueryClient();
const container = document.getElementById("root");

if (container) {
  const root = createRoot(container);

  const renderApp = (Component: React.FC) => {
    root.render(
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => {
          const msg = error instanceof Error ? error.message : String(error);
          const isChunkError =
            msg.includes("dynamically imported module") ||
            msg.includes("Importing a module script failed");
          if (isChunkError) {
            const alreadyReloaded = sessionStorage.getItem("chunk_reloaded");
            return (
              <div style={{ padding: 20, textAlign: "center" }}>
                <p>
                  {alreadyReloaded
                    ? "Failed to load. The app may be temporarily unavailable."
                    : "Loading latest version..."}
                </p>
                <button onClick={() => window.location.reload()}>
                  Refresh
                </button>
              </div>
            );
          }
          return (
            <div role="alert" style={{ padding: 20, textAlign: "center" }}>
              <p>Something went wrong:</p>
              <pre style={{ whiteSpace: "normal" }}>{msg}</pre>
              <button onClick={resetError}>Retry</button>
            </div>
          );
        }}
      >
        <RootContext.Provider value={RootStore}>
          <QueryClientProvider client={queryClient}>
            <StreamI18nProvider>
              <Component />
            </StreamI18nProvider>
          </QueryClientProvider>
        </RootContext.Provider>
      </Sentry.ErrorBoundary>
    );
  };

  import("./routes/AppRoutes").then(({ AppRoutes }) => {
    renderApp(AppRoutes);
  });
}
